import { Notification } from '@/Components/shared/Notifications/types'
import { api } from './api'

export type NotificationResponse = {
  notifications: Notification[]
  total: number
  unreadCount: number
}

export type NotificationStats = {
  total: number
  unread: number
  read: number
  dismissed: number
}

export type CreateNotificationRequest = {
  type: string
  userId: number
  title: string
  message: string
  priority?: 'low' | 'medium' | 'high' | 'urgent'
  metadata?: Record<string, any>
}

export type UpdateNotificationRequest = {
  status?: 'unread' | 'read' | 'dismissed'
  isDeleted?: boolean
}

export type NotificationFilters = {
  type?: string
  status?: string
  priority?: string
  userId?: number
  isDeleted?: boolean
  limit?: number
  offset?: number
  includeDeleted?: boolean
}

/**
 * Marker Notification API service
 * Uses the same endpoint as grooming notifications since they share the same model
 */
export const markerNotificationApi = {
  createNotification: async (
    data: CreateNotificationRequest,
  ): Promise<Notification> => {
    const response = await api.post('/notifications', data)
    return response.data
  },

  getUserNotifications: async (
    userId: number,
    filters?: NotificationFilters,
  ): Promise<NotificationResponse> => {
    const params = new URLSearchParams()

    Object.entries(filters ?? {}).forEach(([key, value]) => {
      if (value !== undefined) {
        params.append(key, value.toString())
      }
    })

    const response = await api.get(
      `/notifications/user/${userId}?${params.toString()}`,
    )
    return response.data
  },

  /**
   * Get notification statistics for a user
   */
  getNotificationStats: async (userId: number): Promise<NotificationStats> => {
    const response = await api.get(
      `/notifications/user/${userId}/stats?type=marker_assigned`,
    )
    return response.data
  },

  /**
   * Update notification
   */
  updateNotification: async (
    id: number,
    data: UpdateNotificationRequest,
  ): Promise<Notification> => {
    const response = await api.put(`/notifications/${id}`, data)
    return response.data
  },

  /**
   * Mark notification as read
   */
  markAsRead: async (id: number): Promise<void> => {
    await api.put(`/notifications/${id}/read`)
  },

  /**
   * Mark all notifications as read for a user
   */
  markAllAsRead: async (userId: number): Promise<void> => {
    await api.put(`/notifications/user/${userId}/read-all?type=marker_assigned`)
  },

  /**
   * Dismiss notification (soft delete)
   */
  dismissNotification: async (id: number): Promise<void> => {
    await api.put(`/notifications/${id}/dismiss`)
  },

  /**
   * Dismiss all notifications for a user
   */
  dismissAllNotifications: async (userId: number): Promise<void> => {
    await api.put(
      `/notifications/user/${userId}/dismiss-all?type=marker_assigned`,
    )
  },

  /**
   * Hard delete a single notification
   */
  hardDeleteNotification: async (id: number): Promise<void> => {
    await api.delete(`/notifications/${id}`)
  },

  /**
   * Hard delete all notifications for a user
   */
  hardDeleteAllNotifications: async (userId: number): Promise<void> => {
    await api.delete(`/notifications/user/${userId}?type=marker_assigned`)
  },
}
